/**
 * header.js — header-component.
 * - Mobile menu drawer: open/close, focus trap, Escape, scroll lock, aria state.
 * - Sticky behavior (data-sticky): "always" pins the header; "scroll_up" reveals
 *   it only when scrolling back up, hidden while scrolling down. A placeholder
 *   reserves the header's height while pinned so content never jumps.
 *
 * Anti-jitter: sampled on rAF, and hide/show only fires after SUSTAINED scroll
 * in one direction (accumulator + thresholds) so small wobbles don't toggle it.
 */
import { Component } from '@theme/component';
import { trapFocus } from '@theme/utilities';

const HIDE_AFTER = 40; // px of continuous downward scroll before hiding
const SHOW_AFTER = 24; // px of continuous upward scroll before revealing

class HeaderComponent extends Component {
  mounted() {
    this._onKey = (event) => {
      if (event.key === 'Escape') this.closeMenu();
    };

    this._headerEl = this.querySelector('.header');
    this._mode = this.dataset.sticky;

    // Expose the header height so sticky content (e.g. the PDP gallery) can
    // offset itself and not sit under the header when it re-pins on scroll-up.
    this._setHeaderHeightVar = () => {
      if (this._headerEl) {
        document.documentElement.style.setProperty('--header-height', `${this._headerEl.offsetHeight}px`);
      }
    };
    this._setHeaderHeightVar();
    window.addEventListener('resize', this._setHeaderHeightVar, { passive: true });

    if (this._mode === 'always') {
      this._pin();
    } else if (this._mode === 'scroll_up' && this._headerEl) {
      const rect = this._headerEl.getBoundingClientRect();
      this._pinAfter = rect.bottom + window.scrollY;
      this._lastY = window.scrollY;
      this._accum = 0;
      this._ticking = false;
      this._onScroll = () => {
        if (this._ticking) return;
        this._ticking = true;
        requestAnimationFrame(() => {
          this._handleScroll();
          this._ticking = false;
        });
      };
      window.addEventListener('scroll', this._onScroll, { passive: true });
    }

    // Predictive search: debounce input → /search/suggest.json.
    if (this.refs.searchInput) {
      this._searchInput = this.refs.searchInput;
      this._searchResults = this.refs.searchResults;
      this._searchInput.addEventListener('input', () => {
        clearTimeout(this._searchTimer);
        this._searchTimer = setTimeout(() => this._predict(this._searchInput.value.trim()), 250);
      });
    }
  }

  unmounted() {
    if (this._searchKey) document.removeEventListener('keydown', this._searchKey);
    document.removeEventListener('keydown', this._onKey);
    document.documentElement.classList.remove('scroll-locked');
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
    if (this._setHeaderHeightVar) window.removeEventListener('resize', this._setHeaderHeightVar);
    this._release?.();
  }

  /* ---- menu drawer ---- */
  openMenu() {
    this.classList.add('menu-open');
    document.documentElement.classList.add('scroll-locked');
    this.refs.burger?.setAttribute('aria-expanded', 'true');
    const panel = this.refs.drawer;
    if (panel) {
      panel.setAttribute('aria-hidden', 'false');
      this._release = trapFocus(panel, this.refs.close || panel);
    }
    document.addEventListener('keydown', this._onKey);
  }

  closeMenu() {
    this.classList.remove('menu-open');
    document.documentElement.classList.remove('scroll-locked');
    this.refs.burger?.setAttribute('aria-expanded', 'false');
    this.refs.drawer?.setAttribute('aria-hidden', 'true');
    this._release?.();
    this._release = null;
    document.removeEventListener('keydown', this._onKey);
    this.refs.burger?.focus();
  }

  /* ---- predictive search ---- */
  toggleSearch() {
    this.classList.contains('search-open') ? this.closeSearch() : this.openSearch();
  }

  openSearch() {
    this.classList.add('search-open');
    this.refs.searchPanel?.removeAttribute('inert');
    this.refs.searchPanel?.setAttribute('aria-hidden', 'false');
    this.refs.searchToggle?.setAttribute('aria-expanded', 'true');
    this._searchKey = (event) => {
      if (event.key === 'Escape') this.closeSearch();
    };
    document.addEventListener('keydown', this._searchKey);
    requestAnimationFrame(() => this._searchInput?.focus());
  }

  closeSearch() {
    this.classList.remove('search-open');
    this.refs.searchPanel?.setAttribute('inert', '');
    this.refs.searchPanel?.setAttribute('aria-hidden', 'true');
    this.refs.searchToggle?.setAttribute('aria-expanded', 'false');
    if (this._searchKey) document.removeEventListener('keydown', this._searchKey);
    this.refs.searchToggle?.focus();
  }

  async _predict(query) {
    if (!this._searchResults) return;
    const root = window.Shopify?.routes?.root || '/';
    if (query.length < 2) {
      this._searchResults.hidden = true;
      this._searchResults.innerHTML = '';
      return;
    }
    try {
      const url = `${root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
      const res = await fetch(url);
      if (!res.ok) return;
      const data = await res.json();
      const products = data.resources?.results?.products || [];
      const items = products
        .map((p) => {
          const img = p.featured_image?.url || p.image || '';
          const imgTag = img ? `<img class="psr__img" src="${img}" alt="" width="48" height="48" loading="lazy">` : '';
          return `<a href="${p.url}" class="psr__item">${imgTag}<span class="psr__title">${p.title}</span></a>`;
        })
        .join('');
      if (items) {
        const all = `<a href="${root}search?q=${encodeURIComponent(query)}&type=product" class="psr__all">${this.dataset.viewAll || 'View all'}</a>`;
        this._searchResults.innerHTML = items + all;
      } else {
        this._searchResults.innerHTML = `<p class="psr__empty">${this.dataset.noResults || 'No results'}</p>`;
      }
      this._searchResults.hidden = false;
    } catch (err) {
      /* ignore network errors */
    }
  }

  /* ---- sticky ---- */
  _pin(instantHide = false) {
    if (this._pinned) return;
    this._pinned = true;
    this.classList.add('is-pinned');
    if (this.refs.placeholder) this.refs.placeholder.style.blockSize = `${this._headerEl.offsetHeight}px`;
    const el = this._headerEl;
    if (instantHide) {
      // Hide with NO transition so the header doesn't flash in then slide out
      // the moment it pins while scrolling down.
      el.style.transition = 'none';
      el.classList.add('header--pinned', 'is-hidden');
      void el.offsetHeight; // commit the hidden state before restoring transition
      el.style.transition = '';
    } else {
      el.classList.add('header--pinned');
    }
  }

  _unpin() {
    if (!this._pinned) return;
    this._pinned = false;
    this.classList.remove('is-pinned');
    if (this.refs.placeholder) this.refs.placeholder.style.blockSize = '';
    this._headerEl.classList.remove('header--pinned', 'is-hidden');
  }

  _handleScroll() {
    const y = Math.max(0, window.scrollY);
    const delta = y - this._lastY;

    // Near the top: fully static and visible.
    if (y <= this._pinAfter) {
      this._unpin();
      this._accum = 0;
      this._lastY = y;
      return;
    }

    // First pin: if we arrived by scrolling down, hide instantly (no flash).
    if (!this._pinned) {
      this._pin(delta >= 0);
      this._accum = 0;
      this._lastY = y;
      return;
    }

    this._lastY = y;
    if (delta === 0) return;

    // Reset the accumulator whenever direction flips, so only *sustained*
    // scrolling in one direction crosses the threshold.
    if ((delta > 0 && this._accum < 0) || (delta < 0 && this._accum > 0)) this._accum = 0;
    this._accum += delta;

    if (this._accum > HIDE_AFTER) {
      this._headerEl.classList.add('is-hidden');
    } else if (this._accum < -SHOW_AFTER) {
      this._headerEl.classList.remove('is-hidden');
    }
  }
}

customElements.define('header-component', HeaderComponent);
